import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "cm-align-center gap-x-10" }
const _hoisted_2 = {
  class: "cm-align-center cm-ui-mt20",
  style: {"flex":"1","justify-content":"flex-end"}
}

import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { User, UserInfo } from '@/services/user'
import { computed, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmsVerifyService } from '@/views/_components/sms-verify/sms-verify.service'
import { PasswordInitService } from '@/views/_components/password-init/password-init.service'
import { ElMessage } from 'element-plus'
import { VncPdType } from '@/services/login.service'
import { passwordHmac } from '@/utils/format'
import CmForm from '@/components/cmForm/index.vue'
import { FormActionType, FormSize } from '@/components/cmForm/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'password-init',
  props: {
  modelValue: {
    type: Boolean,
    default: false,
  },
  isForget: {
    type: Boolean,
    default: false,
  },
},
  emits: ['update:modelValue', 'confirm'],
  setup(__props, { emit: __emit }) {

const props = __props

const emits = __emit

const { t, locale } = useI18n()

const injector = useReflectiveInjector([SmsVerifyService, PasswordInitService])
const smsVerifyService = injector.get(SmsVerifyService)
const passwordInitService = injector.get(PasswordInitService)
const userService = injector.get(User)
const userInfo = reactive<Partial<UserInfo>>({})

const formRef = ref<InstanceType<typeof CmForm>>()
/** 等待验证码时长 0-60 */
const codeWaitSecond = ref(0)

const formData = reactive({
  password: '',
  confirm_pwd: '',
  phone: '',
  code: '',
})

function syncPhoneFromUser (mobile?: string) {
  formData.phone = mobile?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') ?? ''
}

userService.userInfo.subscribe(info => {
  Object.assign(userInfo, info)
  syncPhoneFromUser(info.mobile)
})

const passwordRegex = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])(?=.*[^a-zA-Z0-9]).{8,32}$/

const dialogTitle = computed(() =>
  props.isForget ? t('passwordInit.titleForgotPassword') : t('passwordInit.titleSetPassword'),
)

const formColumns = computed(() => {
  void locale.value

  const validatePassword = (_rule: unknown, _value: string, callback: (e?: Error) => void) => {
    if (!passwordRegex.test(formData.password)) {
      callback(new Error(t('passwordInit.passwordRuleError')))
      return
    }
    callback()
  }

  const validateConfirmPassword = (_rule: unknown, _value: string, callback: (e?: Error) => void) => {
    if (formData.password !== formData.confirm_pwd) {
      callback(new Error(t('passwordInit.confirmPasswordMismatchError')))
      return
    }
    callback()
  }

  return {
    password: {
      label: props.isForget ? t('passwordInit.labelNewPassword') : t('passwordInit.labelPassword'),
      inputType: 'password',
      placeholder: t('passwordInit.passwordPlaceholder'),
      colOpt: { span: 24 },
      inputOpt: { 'show-password': true, autocomplete: 'new-password' },
      rules: [
        {
          required: true,
          message: props.isForget ? t('passwordInit.newPasswordRequired') : t('passwordInit.passwordRequired'),
          trigger: 'blur',
        },
        { custom: { validator: validatePassword }, trigger: ['blur', 'change'] },
      ],
    },
    confirm_pwd: {
      label: t('passwordInit.labelConfirmPassword'),
      inputType: 'password',
      placeholder: t('passwordInit.confirmPasswordPlaceholder'),
      colOpt: { span: 24 },
      inputOpt: { 'show-password': true, autocomplete: 'new-password' },
      rules: [
        { required: true, message: t('passwordInit.confirmPasswordRequired'), trigger: 'blur' },
        { custom: { validator: validateConfirmPassword }, trigger: 'blur' },
      ],
    },
    phone: {
      label: t('passwordInit.labelPhone'),
      placeholder: t('passwordInit.phonePlaceholder'),
      colOpt: { span: 24 },
      inputOpt: { readonly: true },
    },
    code: {
      label: t('passwordInit.labelVerifyCode'),
      slot: true,
      colOpt: { span: 24 },
      rules: [{ required: true, message: t('passwordInit.verifyCodeRequired'), trigger: 'blur' }],
    },
  }
})

async function handleClickSendCode () {
  smsVerifyService.sendCodeUid().then(() => {
    startCodeSecondTimer()
  })
}

async function handleConfirm () {
  if (!await formRef.value?.handleSubmit()) {
    return
  }
  passwordInitService.resetPwd({
    ...formData,
    password: passwordHmac(formData.password),
    confirm_pwd: passwordHmac(formData.confirm_pwd),
    phone: undefined,
  }).then(() => {
    ElMessage.success(t('passwordInit.setPasswordSuccess'))
    userService.updateUserinfo({ ...userInfo, vnc_password: VncPdType.alreadySet })
    emits('confirm', formData)
    handleClose()
  })
}

function handleClose () {
  resetData()
  emits('update:modelValue', false)
}

function resetData () {
  formRef.value?.handleReset()
  formData.password = ''
  formData.confirm_pwd = ''
  formData.code = ''
  syncPhoneFromUser(userInfo.mobile)
  codeWaitSecond.value = 0
}

/** 开始验证码倒数计时 */
function startCodeSecondTimer () {
  codeWaitSecond.value = 60
  const intervalMillisecond = 1000
  const createTimer = () => {
    setTimeout(() => {
      codeWaitSecond.value--
      if (codeWaitSecond.value > 0) {
        createTimer()
      }
    }, intervalMillisecond)
  }
  createTimer()
}

onUnmounted(() => {
  handleClose()
})

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    "append-to-body": "",
    "custom-class": "password-init-dialog password-init-wrap",
    title: dialogTitle.value,
    width: "500px",
    "model-value": __props.modelValue,
    onClose: handleClose
  }, {
    default: _withCtx(() => [
      _createVNode(CmForm, {
        ref_key: "formRef",
        ref: formRef,
        modelValue: formData,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData) = $event)),
        formSize: _unref(FormSize).Small,
        formType: _unref(FormActionType).Other,
        "onUpdate:formType": _cache[2] || (_cache[2] = ($event: any) => ((_unref(FormActionType).Other) = $event)),
        formColumns: formColumns.value,
        formOpt: { 'label-width': 'auto','label-position': 'top', skipSubmitMsg: true }
      }, {
        code: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_el_input, {
              size: "small",
              class: "code-input",
              style: {"flex":"auto"},
              modelValue: formData.code,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.code) = $event)),
              placeholder: _unref(t)('passwordInit.verifyCodePlaceholder')
            }, null, 8, ["modelValue", "placeholder"]),
            _createVNode(_component_el_button, {
              size: "small",
              class: "button-code",
              disabled: codeWaitSecond.value !== 0,
              onClick: handleClickSendCode
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(codeWaitSecond.value === 0
              ? _unref(t)('passwordInit.getVerifyCode')
              : _unref(t)('passwordInit.verifyCodeCountdown', { n: codeWaitSecond.value })), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ]),
        _: 1
      }, 8, ["modelValue", "formSize", "formType", "formColumns"]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_component_el_button, {
          size: "small",
          type: "primary",
          plain: "",
          onClick: handleClose
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('common.cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_el_button, {
          size: "small",
          type: "primary",
          onClick: handleConfirm
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('common.confirm')), 1)
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["title", "model-value"]))
}
}

})